import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, Languages, LayoutGrid, Sparkles, type LucideIcon } from "lucide-react";

interface StatDef {
  key: string;
  target: number;
  suffix: "" | "+";
  icon: LucideIcon;
}

const STATS: StatDef[] = [
  { key: "features", target: 89, suffix: "+", icon: Sparkles },
  { key: "categories", target: 10, suffix: "", icon: LayoutGrid },
  { key: "languages", target: 5, suffix: "", icon: Languages },
  { key: "protocols", target: 20, suffix: "+", icon: BookOpen },
];

const ANIMATION_DURATION_MS = 1400;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useCounter(target: number, start: boolean): number {
  const [value, setValue] = useState<number>(() =>
    start && prefersReducedMotion() ? target : 0,
  );

  useEffect(() => {
    if (!start) return;
    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }
    let frame = 0;
    const startTs = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTs;
      const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, target]);

  return value;
}

function StatTile({ stat, animate }: { stat: StatDef; animate: boolean }) {
  const { t } = useTranslation("landing");
  const value = useCounter(stat.target, animate);
  const Icon = stat.icon;

  return (
    <div className="group relative flex flex-col items-center gap-2 rounded-3xl border border-border/60 bg-card/70 p-5 text-center shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md sm:p-6">
      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <p
        className="font-serif text-3xl font-semibold leading-none tracking-tight text-foreground sm:text-4xl"
        aria-live="polite"
      >
        {value}
        <span className="text-primary">{stat.suffix}</span>
      </p>
      <p className="break-words text-xs font-medium leading-5 text-muted-foreground [hyphens:auto] sm:text-sm">
        {t(`trustBar.items.${stat.key}.label`)}
      </p>
    </div>
  );
}

export function TrustBar() {
  const { t } = useTranslation("landing");
  const ref = useRef<HTMLElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setAnimate(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setAnimate(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative border-y border-border bg-muted/30 py-10 sm:py-14"
      aria-label={t("trustBar.ariaLabel")}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 sm:gap-5 sm:px-6 lg:grid-cols-4 lg:gap-6 lg:px-8">
        {STATS.map((stat) => (
          <StatTile key={stat.key} stat={stat} animate={animate} />
        ))}
      </div>
    </section>
  );
}

export default TrustBar;
